import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { loadCart, saveCart, clearCartForUser, getCartKey } from '../utils/cartStorage';
import logger from '../utils/logger';

const CartContext = createContext();

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
};

export const CartProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [cartItems, setCartItems] = useState([]);

  // Load cart when user changes
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setCartItems([]);
      return;
    }
    
    // Debug: Log user info to help troubleshoot cart saving
    if (process.env.NODE_ENV === 'development') {
      logger.debug('Loading cart for user:', { id: user?.id, _id: user?._id, email: user?.email });
    }
    
    const items = loadCart(user);
    const validItems = Array.isArray(items) ? items : [];
    
    if (process.env.NODE_ENV === 'development') {
      logger.debug('Loaded cart items:', validItems.length);
    }
    
    setCartItems(validItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, user?._id, user?.email]);

  // Persist when cart changes
  useEffect(() => {
    if (isAuthenticated && user) {
      try {
        saveCart(user, cartItems);
        if (process.env.NODE_ENV === 'development') {
          logger.debug('Cart saved:', cartItems.length, 'items for user:', user?.id || user?._id || user?.email);
        }
      } catch (error) {
        logger.error('Error saving cart:', error);
      }
    }
  }, [cartItems, isAuthenticated, user]);

  // Cross-tab sync for current user
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === getCartKey(user)) {
        const next = loadCart(user);
        setCartItems(Array.isArray(next) ? next : []);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [user]);

  const addItem = (item, quantity = 1) => {
    // Optimistic update: store previous state for rollback
    const previousCartItems = [...cartItems];
    
    try {
      let updatedItems;
      
      setCartItems((prev) => {
        // Check for existing item considering variations
        // If item has variationId, match both product ID and variationId
        // If item doesn't have variationId, match product ID and ensure existing item also doesn't have variationId
        const idx = prev.findIndex((i) => {
          // Check if same product (handle both _id and productId fields)
          const sameProductId = 
            i._id === item._id || 
            i.productId === item._id || 
            i._id === item.productId ||
            (i.productId && item.productId && i.productId === item.productId);
          
          if (item.variationId) {
            // Item has variation - must match both product ID and variation ID
            return sameProductId && i.variationId === item.variationId;
          } else {
            // Item has no variation - must match product ID and existing item must also have no variation
            return sameProductId && !i.variationId;
          }
        });
        
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], quantity: (next[idx].quantity || 1) + quantity };
          updatedItems = next;
          return next;
        }
        updatedItems = [...prev, { ...item, quantity }];
        return updatedItems;
      });
      
      // Save to storage
      try {
        saveCart(user, updatedItems);
      } catch (error) {
        // Rollback on error
        logger.error('Error saving cart after adding item:', error);
        setCartItems(previousCartItems);
        throw error;
      }
    } catch (error) {
      // Rollback on any error
      logger.error('Error adding item to cart:', error);
      setCartItems(previousCartItems);
      throw error;
    }
  };

  const updateItemQuantity = (productId, quantity) => {
    // Optimistic update: store previous state for rollback
    const previousCartItems = [...cartItems];
    
    try {
      const updatedItems = cartItems.map((i) => 
        (i._id === productId ? { ...i, quantity: Math.max(1, quantity) } : i)
      );
      setCartItems(updatedItems);
      
      // Save to storage
      try {
        saveCart(user, updatedItems);
      } catch (error) {
        // Rollback on error
        logger.error('Error saving cart after quantity update:', error);
        setCartItems(previousCartItems);
        throw error;
      }
    } catch (error) {
      // Rollback on any error
      logger.error('Error updating item quantity:', error);
      setCartItems(previousCartItems);
      throw error;
    }
  };

  const removeItem = (productId) => {
    // Optimistic update: store previous state for rollback
    const previousCartItems = [...cartItems];
    
    try {
      const updatedItems = cartItems.filter((i) => i._id !== productId);
      setCartItems(updatedItems);
      
      // Save to storage
      try {
        saveCart(user, updatedItems);
      } catch (error) {
        // Rollback on error
        logger.error('Error saving cart after item removal:', error);
        setCartItems(previousCartItems);
        throw error;
      }
    } catch (error) {
      // Rollback on any error
      logger.error('Error removing item from cart:', error);
      setCartItems(previousCartItems);
      throw error;
    }
  };

  const clearCart = () => {
    clearCartForUser(user);
    setCartItems([]);
  };

  // Allow pages like CartPage to sync local cart changes back into context
  const syncCartFromPage = (items) => {
    if (!Array.isArray(items)) return;
    setCartItems(items);
  };

  const value = useMemo(
    () => ({
      cartItems,
      addItem,
      updateItemQuantity,
      removeItem,
      clearCart,
      syncCartFromPage,
    }),
    [cartItems]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};


